import { z } from 'zod';
import { rawItemListItemSchema } from '@/api/contracts/items';
import { defineEndpoint } from './base';

export const favoriteItemIdSchema = z.number().int().positive();

export const favoriteIdsResponseSchema = z.object({
  itemIds: z.array(favoriteItemIdSchema).default([]),
  count: z.number().int().nonnegative().default(0),
});

export const favoriteMutationRequestSchema = z.object({
  itemId: favoriteItemIdSchema,
});

export const favoriteMutationResponseSchema = z.object({
  success: z.boolean(),
  itemId: favoriteItemIdSchema,
  favorited: z.boolean(),
  createdAt: z.string().optional(),
  savedCount: z.number().int().nonnegative().default(0),
});

export const rawFavoriteListItemSchema = rawItemListItemSchema.extend({
  isFavorited: z.boolean().default(true),
  favoritedAt: z.string().optional(),
});

export const rawFavoriteListResponseSchema = z.object({
  items: z.array(rawFavoriteListItemSchema).default([]),
  itemIds: z.array(favoriteItemIdSchema).default([]),
  count: z.number().int().nonnegative().default(0),
});

export const favoriteItemIdPathParamsSchema = z.object({
  itemId: favoriteItemIdSchema,
});

export type FavoriteItemIdPathParams = z.infer<typeof favoriteItemIdPathParamsSchema>;
export type FavoriteIdsResponse = z.infer<typeof favoriteIdsResponseSchema>;
export type FavoriteMutationRequest = z.infer<typeof favoriteMutationRequestSchema>;
export type FavoriteMutationResponse = z.infer<typeof favoriteMutationResponseSchema>;
export type RawFavoriteListItem = z.infer<typeof rawFavoriteListItemSchema>;
export type RawFavoriteListResponse = z.infer<typeof rawFavoriteListResponseSchema>;

export const FAVORITES_CONTRACT = {
  list: defineEndpoint<void, RawFavoriteListResponse>({
    method: 'GET',
    path: '/me/favorites',
    auth: 'authenticated',
    schemas: {
      response: rawFavoriteListResponseSchema,
    },
  }),
  ids: defineEndpoint<void, FavoriteIdsResponse>({
    method: 'GET',
    path: '/me/favorites/ids',
    auth: 'authenticated',
    schemas: {
      response: favoriteIdsResponseSchema,
    },
  }),
  add: defineEndpoint<FavoriteMutationRequest, FavoriteMutationResponse>({
    method: 'POST',
    path: '/me/favorites',
    auth: 'authenticated',
    schemas: {
      request: favoriteMutationRequestSchema,
      response: favoriteMutationResponseSchema,
    },
  }),
  remove: defineEndpoint<void, FavoriteMutationResponse, FavoriteItemIdPathParams>({
    method: 'DELETE',
    path: '/me/favorites/:itemId',
    auth: 'authenticated',
    schemas: {
      response: favoriteMutationResponseSchema,
      pathParams: favoriteItemIdPathParamsSchema,
    },
  }),
} as const;
